// Web Push subscription helper.
// Registers a dedicated Service Worker (sn-push-sw.js) and manages
// permission + subscription lifecycle.
import api from "./api";

const SW_PATH = "/sn-push-sw.js";
const SW_SCOPE = "/";

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPermissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

async function ensureRegistration() {
  const existing = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (existing && existing.active) return existing;
  return await navigator.serviceWorker.register(SW_PATH, { scope: SW_SCOPE });
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  const reg = await ensureRegistration();
  return reg.pushManager.getSubscription();
}

/**
 * Subscribe the browser and persist on the backend.
 * Returns the PushSubscription or throws.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Push non supporté sur ce navigateur");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Autorisation refusée");
  }
  const reg = await ensureRegistration();
  // Fetch VAPID public key from backend
  const { data } = await api.get("/push/vapid-public-key");
  const applicationServerKey = urlBase64ToUint8Array(data.public_key);
  const existing = await reg.pushManager.getSubscription();
  const sub = existing || await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
  // Persist server-side
  await api.post("/push/subscribe", sub.toJSON());
  return sub;
}

export async function unsubscribeFromPush() {
  const sub = await getExistingSubscription();
  if (!sub) return { removed: 0 };
  try {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  } catch { /* backend cleanup ok even if it fails */ }
  await sub.unsubscribe();
  return { removed: 1 };
}

export async function sendTestPush() {
  return api.post("/push/test");
}
